import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Save, ClipboardList, CalendarCheck } from "lucide-react";

const empty = {
  title: "", date: new Date().toISOString().slice(0, 16), status: "prepare",
  prep_org_info: "", prep_history: "", prep_needs: "", prep_prev_requests: "", prep_availability: "",
  prep_obj1: "", prep_obj2: "", prep_obj3: "", prep_docs: "", prep_questions: "", prep_next: "",
  rep_participants: "", rep_needs: "", rep_objections: "", rep_interest: "", rep_info_promised: "",
  rep_docs_sent: "", rep_result: "", rep_next_action: "", rep_relance_date: "", rep_self_eval: "", rep_comment: "",
};
const STATUSES = ["prepare", "planifie", "realise", "cloture"];

const F = ({ label, k, form, set, area, type }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {area ? <Textarea rows={2} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`mtg-${k}`} />
      : <Input type={type || "text"} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`mtg-${k}`} />}
  </div>
);

export default function PartnerMeetingDialog({ partnerId, meeting, open, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(meeting ? { ...empty, ...meeting } : empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const showReport = form.status === "realise" || form.status === "cloture";

  const save = async () => {
    try {
      const body = { ...form, date: form.date ? new Date(form.date).toISOString() : "" };
      if (meeting?.id) await api.put(`/crm/partners/${partnerId}/meetings/${meeting.id}`, body);
      else await api.post(`/crm/partners/${partnerId}/meetings`, body);
      toast.success(t("crm.partner.saved")); onSaved?.(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="partner-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-fuchsia-400" /> {t("crm.meeting.section")}</DialogTitle>
          <DialogDescription className="sr-only">{t("crm.meeting.section")}</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2"><F label={t("crm.meeting.title")} k="title" form={form} set={set} /></div>
          <F label={t("crm.meeting.date")} k="date" form={form} set={set} type="datetime-local" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t("crm.meeting.status")}</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="mtg-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`crm.meeting.st_${s}`)}</SelectItem>)}</SelectContent>
          </Select>
          {form.status === "cloture" && !(form.rep_result?.trim() && (form.rep_next_action?.trim() || form.rep_comment?.trim())) && (
            <p className="text-[11px] text-rose-400" data-testid="mtg-report-required">{t("crm.meeting.reportRequired")}</p>
          )}
        </div>

        {/* Préparation */}
        <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold flex items-center gap-1.5 pt-1"><ClipboardList className="h-4 w-4" /> {t("crm.meeting.prepTitle")}</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <F label={t("crm.meeting.orgInfo")} k="prep_org_info" form={form} set={set} area />
          <F label={t("crm.meeting.history")} k="prep_history" form={form} set={set} area />
          <F label={t("crm.meeting.needs")} k="prep_needs" form={form} set={set} area />
          <F label={t("crm.meeting.prevRequests")} k="prep_prev_requests" form={form} set={set} area />
          <F label={t("crm.meeting.availability")} k="prep_availability" form={form} set={set} />
          <F label={t("crm.meeting.docs")} k="prep_docs" form={form} set={set} />
          <F label={t("crm.meeting.obj1")} k="prep_obj1" form={form} set={set} />
          <F label={t("crm.meeting.obj2")} k="prep_obj2" form={form} set={set} />
          <F label={t("crm.meeting.obj3")} k="prep_obj3" form={form} set={set} />
          <F label={t("crm.meeting.next")} k="prep_next" form={form} set={set} />
          <div className="sm:col-span-2"><F label={t("crm.meeting.questions")} k="prep_questions" form={form} set={set} area /></div>
        </div>

        {/* Compte-rendu (visible si réalisé/clôturé) */}
        {showReport && (
          <>
            <div className="text-xs uppercase tracking-wider text-fuchsia-400 font-semibold flex items-center gap-1.5 pt-1"><ClipboardList className="h-4 w-4" /> {t("crm.meeting.reportTitle")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label={t("crm.meeting.participants")} k="rep_participants" form={form} set={set} />
              <F label={t("crm.meeting.result")} k="rep_result" form={form} set={set} />
              <F label={t("crm.meeting.needs")} k="rep_needs" form={form} set={set} area />
              <F label={t("crm.meeting.objections")} k="rep_objections" form={form} set={set} area />
              <F label={t("crm.meeting.interest")} k="rep_interest" form={form} set={set} />
              <F label={t("crm.meeting.selfEval")} k="rep_self_eval" form={form} set={set} />
              <F label={t("crm.meeting.infoPromised")} k="rep_info_promised" form={form} set={set} />
              <F label={t("crm.meeting.docsSent")} k="rep_docs_sent" form={form} set={set} />
              <F label={t("crm.meeting.nextAction")} k="rep_next_action" form={form} set={set} />
              <F label={t("crm.meeting.relanceDate")} k="rep_relance_date" form={form} set={set} type="date" />
              <div className="sm:col-span-2"><F label={t("crm.meeting.comment")} k="rep_comment" form={form} set={set} area /></div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("crm.actions.cancel")}</Button>
          <Button onClick={save} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="mtg-save"><Save className="h-4 w-4 mr-2" /> {t("crm.actions.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
